import type {ResultsKeysAndValues} from 'common/query-builder/query-report-header/types';
import type {NotificationWindow, NotificationWindows} from 'app_api/projects/alerts/custom/types';
import type {FlowsBookmark} from 'common/report/flows/models/types';
import type {FunnelBookmark} from 'common/report/funnels/models/types';
import type {InsightsBookmarkParams} from 'common/types/reports/bookmark';

// Custom alerts are only supported on Insights, Funnels, and Flows reports
export type CustomAlertBookmarkParams = InsightsBookmarkParams | FunnelBookmark | FlowsBookmark;

export enum SubscriptionType {
  Email = `email`,
  SlackWebhook = `slack_webhook`,
  SlackChannel = `slack_channel`,
  ProjectWebhook = `project_webhook`,
}

export interface EmailSubscription {
  error?: any;
  data: {
    recipient: string;
  };
  type: SubscriptionType.Email;
}

export interface SlackWebookSubscription {
  error?: SlackWebhookError;
  data: {
    slack_webhook_url: string;
  };
  type: SubscriptionType.SlackWebhook;
}

export interface SlackChannelSubscription {
  data: {
    slack_team_id: string;
    slack_channel_id: string;
    slack_channel_name: string;
  };
  type: SubscriptionType.SlackChannel;
}

export interface ProjectWebhookSubscription {
  error?: any;
  data: {
    webhook_id: string;
    webhook_name: string; // For UI display only, not sent to backend
  };
  type: SubscriptionType.ProjectWebhook;
}

export function isEmailSubscription(sub: Subscription): sub is EmailSubscription {
  return sub.type === SubscriptionType.Email;
}

export type Subscription =
  | EmailSubscription
  | SlackWebookSubscription
  | SlackChannelSubscription
  | ProjectWebhookSubscription;

export enum SlackWebhookError {
  InvalidUrl = `Not a valid Slack webhook url`,
}

// Raw API response type for custom alerts
export interface RawCustomAlertResponse {
  id: number;
  name: string;
  paused: boolean;
  frequency: number;
  valid?: boolean;
  last_fired: string | null;
  results: {
    fired_count: number;
  } | null;
  condition: CustomAlertCondition;
  subscriptions: Subscription[];
  bookmark: {
    id: number;
    name: string;
    type: string;
  };
  creator: {
    id: number;
    name: string;
  };
  created: string;
  modified: string;
  project: {
    id: number;
    name: string;
  };
  workspace?: {
    id: number;
    name: string;
  };
  notification_windows?: NotificationWindows;
}
export type CustomAlertCondition = {
  keys: TODO; // can be an array of object or string
  op: string;
  type: string;
  value: number;
  confidence?: number;
  trainingMode?: string;
};

// React component types

// Threshold operator options for the dropdown
export const thresholdOperators = [
  {
    value: `isAbove`,
    label: `is above`,
    isRelative: false,
    alwaysEnabled: true,
    backendOp: `>`,
  },
  {
    value: `isBelow`,
    label: `is below`,
    isRelative: false,
    alwaysEnabled: true,
    backendOp: `<`,
  },
  {
    value: `increasesBy`,
    label: `increases by more than`,
    isRelative: true,
    alwaysEnabled: false,
    backendOp: `>`,
  },
  {
    value: `decreasesBy`,
    label: `decreases by more than`,
    isRelative: true,
    alwaysEnabled: false,
    backendOp: `<`,
  },
] as const;

export type ThresholdOperator = (typeof thresholdOperators)[number];

export interface AlertKey {
  displayHeader: string;
  displayValue: string | null;
  header: string;
  value: string | null;
}

export type SubscriptionsMap = {
  [SubscriptionType.Email]: EmailSubscription[];
  [SubscriptionType.SlackWebhook]: SlackWebookSubscription[];
  [SubscriptionType.SlackChannel]: SlackChannelSubscription[];
  [SubscriptionType.ProjectWebhook]: ProjectWebhookSubscription[];
};

// Form data shape for react-hook-form based modal
export interface CustomAlertFormData {
  alertName: string;
  alertThreshold: number | null;
  // Alert configuration
  isAnomalyDetection: boolean; // true = anomaly detection mode, false = threshold mode
  operator: ThresholdOperator; // The selected threshold operator
  isPercentage: boolean; // true = threshold is percentage (only for relative operators)
  alertFrequency: string;
  confidenceInterval: number;
  trainingMode: string;
  selectedAlertKeys: AlertKey[];
  subscriptions: SubscriptionsMap;
  workspaceId: number | null;
  notificationWindows: {enabled: boolean; windows?: Array<Partial<NotificationWindow>>};
}

// API error shape from processResponse
export type ApiError = {error: string; status: number};

// Result returned from promptCustomAlertModal
export interface CustomAlertModalResult {
  action: `cancel` | `create` | `update`;
  result?: RawCustomAlertResponse;
  error?: ApiError;
}

// Config passed to promptCustomAlertModal
export interface CustomAlertModalConfig {
  bookmarkId: number;
  projectId: number;
  workspaceId: number | undefined;
  resultsKeysAndValues: ResultsKeysAndValues[] | null;
  dateRangeIntervalDays: number | null;
  editingAlert?: RawCustomAlertResponse;
  readOnly?: boolean;
}
